import type { AtsFilterDefinition } from "@/types/ats-explorer-api"
import type { TranslationKey } from "@/lib/translations"

export interface FilterGroup {
  key: string
  /** String key for the section heading; the blade translates it */
  titleKey: TranslationKey
  definitions: AtsFilterDefinition[]
  /** Collapsed by default in the filter blade */
  collapsed: boolean
}

const GROUP_ORDER: { key: string; titleKey: TranslationKey; collapsed: boolean }[] = [
  { key: "required", titleKey: "explorer.filters.groups.required", collapsed: false },
  { key: "identity", titleKey: "explorer.filters.groups.identity", collapsed: false },
  { key: "dates", titleKey: "explorer.filters.groups.dates", collapsed: false },
  { key: "people", titleKey: "explorer.filters.groups.people", collapsed: false },
  { key: "sort", titleKey: "explorer.filters.groups.sort", collapsed: true },
  { key: "other", titleKey: "explorer.filters.groups.other", collapsed: true },
  { key: "custom", titleKey: "explorer.filters.groups.custom", collapsed: true },
]

const MEMBERSHIP_OPERATORS = new Set(["memberOf", "notMemberOf", "isEmpty", "isNotEmpty"])

function groupKeyFor(definition: AtsFilterDefinition): string {
  if (definition.isRequired) return "required"
  if (definition.isCustom) return "custom"
  if (/sort/i.test(definition.name)) return "sort"
  if (definition.dataType === "datetime") return "dates"
  if (definition.dataType === "reference" || /\.id$/.test(definition.name) || definition.supportedOperators.some((op) => MEMBERSHIP_OPERATORS.has(op))) {
    return "people"
  }
  if (definition.dataType === "boolean" || definition.referenceType || /^(id|status|type|name|title)$/i.test(definition.name)) {
    return "identity"
  }
  return "other"
}

/**
 * Sections for the filter blade. Vendors with metadata-derived filters (Bullhorn)
 * offer 200+ definitions per entity; grouping by what the filter is about keeps the
 * blade scannable, and custom fields stay folded until asked for. Definitions keep
 * their catalog order inside each group.
 */
export function groupFilterDefinitions(definitions: AtsFilterDefinition[], query = ""): FilterGroup[] {
  const term = query.trim().toLowerCase()
  const buckets = new Map<string, AtsFilterDefinition[]>()

  for (const definition of definitions) {
    if (term && !definition.displayName.toLowerCase().includes(term) && !definition.name.toLowerCase().includes(term)) continue
    const key = groupKeyFor(definition)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(definition)
    else buckets.set(key, [definition])
  }

  return GROUP_ORDER.filter((group) => buckets.has(group.key)).map((group) => ({
    ...group,
    // Searching opens everything; a group that holds a match must be visible
    collapsed: term ? false : group.collapsed,
    definitions: buckets.get(group.key)!,
  }))
}
